import { Checkbox, Divider, Group, ScrollArea, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentFinancialResponse } from '../../../api/events-client/events-client.type';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { ResetFiltersButton } from '../../../components/filters/reset-filters-button';
import { snakeCaseToTitleCase } from '../../../helpers/string-helpers/string-helpers';
import { useAgenticProcessStore } from '../../../store/agentic-process-store/agentic-process-store.hooks';
import { newCOLORS } from '../../../styles/colors';
import { Icon } from '../../../ui-library/icon/icon';
import { Select } from '../../../ui-library/select/select';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { assignColorsToItems, getItemColor } from '../../financials/charts/shared/shared.helpers';
import { DEFAULT_CHART_COLORS } from '../../financials/constants';
import { AgenticFinancialsChartCategory } from './agentic-financials.type';

interface AgenticFinancialsFiltersProps {
  data?: AgentFinancialResponse;
  isPending: boolean;
}

export function AgenticFinancialsFilters({ data, isPending }: AgenticFinancialsFiltersProps) {
  const { t } = useTranslation();
  const [isOptionsOpen, { toggle: toggleOptions }] = useDisclosure(true);
  const {
    selectedChartCategory,
    selectedChartOptions,
    setSelectedChartCategory,
    setSelectedChartOptions,
    resetFinancialFilters,
  } = useAgenticProcessStore();

  const categoryOptions = useMemo(
    () => [
      { value: AgenticFinancialsChartCategory.Tasks, label: t('common.work_units') },
      { value: AgenticFinancialsChartCategory.Users, label: t('common.users') },
      { value: AgenticFinancialsChartCategory.Epics, label: t('common.epics') },
      { value: AgenticFinancialsChartCategory.Initiatives, label: t('common.initiatives') },
    ],
    [t],
  );

  const availableOptions = useMemo<{ code: string; name: string }[]>(() => {
    const items = data?.[selectedChartCategory] as Array<{ code?: string; name: string }> | undefined;
    if (!items) return [];
    return items.map((item) => ({ code: item.code ?? item.name, name: item.name }));
  }, [data, selectedChartCategory]);

  useEffect(() => {
    if (selectedChartOptions?.length === 0 && availableOptions.length > 0) {
      setSelectedChartOptions(availableOptions.map((option) => option.code).slice(0, DEFAULT_CHART_COLORS.length));
    }
  }, [availableOptions, selectedChartOptions?.length, setSelectedChartOptions]);

  // Create the same color assignments as used in the chart
  const colorAssignments = assignColorsToItems(selectedChartOptions || [], DEFAULT_CHART_COLORS);

  const hasFilters = selectedChartCategory !== AgenticFinancialsChartCategory.Tasks || selectedChartOptions?.length > 0;

  return (
    <Stack h="100%">
      <Group>
        <span style={{ flex: 1 }}>
          <Select
            value={selectedChartCategory}
            options={categoryOptions}
            placeholder="Select Category"
            onChange={(value) => {
              if (value) {
                setSelectedChartCategory(value as AgenticFinancialsChartCategory);
                setSelectedChartOptions([]);
              }
            }}
          />
        </span>
      </Group>
      <Divider />
      {!isPending && (!data || Object.keys(data).length === 0) ? (
        <ContentPlaceholder icon="event_busy" text={t('common.labels.no_filtered_data')} />
      ) : (
        <Fragment>
          <Group justify="space-between">
            <Group>
              <Icon name="adjust" size={24} color={newCOLORS.gray} />
              <Text weight="bold">
                {categoryOptions.find((opt) => opt.value === selectedChartCategory)?.label ||
                  snakeCaseToTitleCase(selectedChartCategory ?? '')}
              </Text>
            </Group>
            <Icon
              name={isOptionsOpen ? 'unfold_less' : 'unfold_more'}
              size={24}
              color={newCOLORS.gray}
              onClick={toggleOptions}
              style={{ cursor: 'pointer' }}
              pointer
            />
          </Group>
          <Divider />
          <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
            {data &&
            selectedChartCategory &&
            data[selectedChartCategory] &&
            availableOptions.length > 0 &&
            isOptionsOpen ? (
              <Checkbox.Group value={selectedChartOptions} onChange={(value) => setSelectedChartOptions(value)}>
                {availableOptions.map((option, index) => {
                  const color = getItemColor(option.code, colorAssignments);
                  return (
                    <Tooltip key={index} label={option.name} variant="black" multiline w={300}>
                      <Group gap="xs" style={{ alignItems: 'center', marginBottom: '8px' }}>
                        <Checkbox
                          key={index}
                          value={option.code}
                          color={color}
                          styles={{
                            input: { cursor: 'pointer' },
                            label: { marginBottom: '8px' },
                          }}
                          disabled={
                            selectedChartOptions?.length >= DEFAULT_CHART_COLORS.length &&
                            !selectedChartOptions?.includes(option.code)
                          }
                        />
                        <Text style={{ cursor: 'default' }}>{option.code || 'Unknown'}</Text>
                      </Group>
                    </Tooltip>
                  );
                })}
              </Checkbox.Group>
            ) : (
              isOptionsOpen && (
                <Stack align="center" p="md">
                  <Text size="small" color={newCOLORS.gray} style={{ textAlign: 'center' }}>
                    {t('financials.no_filtered_data')}
                  </Text>
                </Stack>
              )
            )}
          </ScrollArea>
        </Fragment>
      )}
      <ResetFiltersButton onClick={() => resetFinancialFilters()} visible={hasFilters} />
    </Stack>
  );
}
